"use client";

import { cloneElement, useMemo, useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export function Gallary({
  categories,
}: {
  categories?: Array<{
    title?: string;
    icon?: React.JSX.Element;
    items?: Array<{
      title?: string;
      description?: string;
      image?: string;
    }>;
  }>;
}) {
  const [activeTab, setActiveTab] = useState("");

  const selectedItems = useMemo(() => {
    const selectedCategories =
      activeTab === ""
        ? categories
        : categories?.filter((category) => category.title === activeTab);
    return (selectedCategories ?? []).flatMap((category) =>
      (category.items ?? []).map((item) => ({ ...item, category }))
    );
  }, [activeTab, categories]);

  return (
    <>
      <Tabs className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-center mb-8">
          <TabsList className="grid grid-cols-2 sm:grid-cols-5 h-auto w-full sm:w-auto">
            <TabsTrigger value="" className="px-4">
              All
            </TabsTrigger>
            {categories?.map((category) => (
              <TabsTrigger
                key={category.title}
                value={category.title || ""}
                className="px-4"
              >
                {!!category.icon &&
                  cloneElement(category.icon, { className: "w-4 h-4 mr-2" })}
                {category.title}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value={activeTab} className="mt-0">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {selectedItems.map(
              ({ category, ...item }, index) =>
                !!item && (
                  <GalleryCard
                    key={item.title || index}
                    category={category}
                    item={item}
                  />
                )
            )}
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
}

function GalleryCard({
  category,
  item,
}: {
  category: { title?: string; icon?: React.JSX.Element };
  item: { title?: string; description?: string; image?: string };
}) {
  return (
    <Dialog>
      <DialogTrigger className="group overflow-hidden rounded-lg cursor-pointer transition-all hover:shadow-md focus:shadow-md hover:translate-y-1 focus:translate-y-1">
        <div className="relative">
          <img
            src={item.image || "/placeholder.svg"}
            alt=""
            className="w-full object-cover transition-transform duration-300 group-hover:scale-105 group-focus:scale-105 aspect-square"
          />
          <div className="absolute bottom-0 left-0 right-0 p-3 text-white bg-gradient-to-t from-black/70 to-transparent">
            <h3 className="text-sm font-medium">{item.title}</h3>
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-transparent border-none">
        <div className="relative bg-white rounded-lg">
          <div className="flex flex-col md:flex-row">
            <img
              src={item.image || "/placeholder.svg"}
              alt=""
              className="object-cover w-full md:w-2/3 aspect-[4/3]"
            />
            <div className="p-6 md:w-1/3">
              <DialogTitle className="mb-2 text-xl font-semibold">
                {item.title}
              </DialogTitle>
              <DialogDescription className="mb-4 text-zinc-600">
                {item.description}
              </DialogDescription>
              <div className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-emerald-50 text-emerald-700">
                {!!category.icon &&
                  cloneElement(category.icon, {
                    className: "w-3.5 h-3.5 mr-1",
                  })}
                {(category.title?.charAt(0).toUpperCase() ?? "") +
                  (category.title?.slice(1) ?? "")}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
